"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useId, useState, Suspense } from "react";
import { useRouter } from "next/navigation";
import { parseAsString } from "nuqs";
import { type IQuerySearchParam } from "@/types/query-search-params";
import { useQuerySearchParams } from "@/hooks/use-query-search-params";
import { useMutation } from "@tanstack/react-query";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  SkeletonBox,
  SkeletonContainer,
  SkeletonLabel,
} from "@/components/ui/skeleton";
import { AuthPageHeader, AuthPageHeaderSkeleton } from "../page-header";
import { Button, SubmitButton } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/input";
import { showToast } from "@/components/ui/sonner";
import { resetPassword } from "@/lib/api/generated";
import { ApiError } from "@/lib/api/error";
import { Icon } from "@/components/ui/icon";
import { PasswordStrength } from "../password-strength";
import { passwordFieldSchema } from "../../lib/password-policy";

const queryParams = {
  TOKEN: {
    field: "token",
    parser: parseAsString.withDefault(""),
  },
} satisfies Record<string, IQuerySearchParam>;

const resetSchema = z.object({ password: passwordFieldSchema });

type ResetValues = z.infer<typeof resetSchema>;

function ResetPasswordContent() {
  const router = useRouter();
  const [token] = useQuerySearchParams(queryParams.TOKEN);
  const [done, setDone] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const serverErrorId = useId();
  const requirementsId = useId();

  const form = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    mode: "all",
    defaultValues: { password: "" },
  });

  const resetMutation = useMutation({
    mutationFn: (password: string) => resetPassword({ token, password }),
    onSuccess: () => {
      setDone(true);
      showToast.success("Password reset", { description: "Your password has been updated." });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "RESET_PASSWORD_REJECTED") {
        setServerError(err.message);
        return;
      }
      if (err instanceof ApiError && err.code === "RESET_TOKEN_INVALID") {
        setLinkInvalid(true);
        return;
      }
      const message = err instanceof Error ? err.message : "Failed to reset password.";
      showToast.error("Reset failed", { description: message });
    },
  });

  function handleSubmit(values: ResetValues) {
    setServerError(null);
    resetMutation.mutate(values.password);
  }

  if (!token || linkInvalid) {
    return (
      <>
        <AuthPageHeader
          title="Invalid Reset Link"
          subtitle="This password reset link is invalid or has expired."
        />
        <Button type="button" variant="linear-ai" size="auth" onClick={() => router.push("/forgot-password")}>
          Request a new link
        </Button>
      </>
    );
  }

  if (done) {
    return (
      <>
        <AuthPageHeader
          title="Password Updated"
          subtitle="Your password has been reset successfully. You can now sign in."
        />
        <Button type="button" variant="linear-ai" size="auth" onClick={() => router.push("/login")}>
          Sign in <Icon name="IconArrowRight" size={16} />
        </Button>
      </>
    );
  }

  return (
    <>
      <AuthPageHeader
        title="Set a new password"
        subtitle="Enter your new password below."
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel required>New Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    className="rounded-sm"
                    aria-invalid={fieldState.invalid || Boolean(serverError)}
                    aria-describedby={
                      serverError
                        ? `${serverErrorId} ${requirementsId}`
                        : requirementsId
                    }
                    {...field}
                    onChange={(event) => {
                      setServerError(null);
                      field.onChange(event);
                    }}
                  />
                </FormControl>
                <PasswordStrength id={requirementsId} password={field.value} />
                {serverError ? (
                  <p
                    id={serverErrorId}
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {serverError}
                  </p>
                ) : null}
              </FormItem>
            )}
          />

          <SubmitButton type="submit" variant="linear-ai" size="auth" loading={resetMutation.isPending} disabled={resetMutation.isPending || !form.formState.isValid}>
            Reset Password <Icon name="IconArrowRight" size={16} />
          </SubmitButton>
        </form>
      </Form>
    </>
  );
}

function ResetPasswordSkeleton() {
  return (
    <>
      <AuthPageHeaderSkeleton subtitleLines={1} />
      <SkeletonContainer className="flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <SkeletonLabel className="w-28" />
          <SkeletonBox className="h-9 w-full rounded-md" />
        </div>
        <SkeletonBox className="h-10 w-full rounded-md" />
      </SkeletonContainer>
    </>
  );
}

export function ResetPassword() {
  return (
    <Suspense fallback={<ResetPasswordSkeleton />}>
      <ResetPasswordContent />
    </Suspense>
  );
}
